import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PageFooter from '../components/PageFooter';
import InstructionsModal from '../components/InstructionsModal';
import SalonHeaderActions from '../components/SalonHeaderActions';
import { useSkinAnalysisPlan } from '../context/SkinAnalysisPlanContext';
import { initializeSession, createNewSessionWithBackend, clearSession, getSessionId, getSessionIdFromUrl, pathWithSessionId, SESSION_STORAGE_KEY, finalizeCustomerSession } from '../utils/sessionManager';
import { saveSalonUserProfile } from '../utils/dataSaver';
import { lookupSalonCustomerByPhone, checkSalonMobileExists } from '../utils/salonCustomerApi';
import { clearCache } from '../utils/dataCache';
import { useSkinSession } from '../context/SkinSessionContext';
import { salonLastCustomerStorageKey } from '../config/salonApp';
import { SESSION_APP_DATA_BASE } from '../config/sessionAppData';
import { getApiBaseUrl } from '../utils/apiConfig';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';
import CountryWhatsAppInput from '../../shared/components/CountryWhatsAppInput';
import {
  DEFAULT_WHATSAPP_COUNTRY,
  getCountryIso2FromPhone,
  getWhatsAppValidationMessage,
  normalizeWhatsAppValue,
  validateWhatsAppNumberByCountry,
} from '../../shared/utils/whatsappPhone';
import './UserInfoPage.css';
import '../styles/PageBackground.css';
import '../styles/ActionButtons.css';
import SkinTypeCarousel from '../components/SkinTypeCarousel';

const genderImageFile = (g) => (g === 'Transgender' ? 'trans.png' : `${(g || '').toLowerCase()}.png`);

const DEFAULT_SKIN_OPTIONS = ['Normal', 'Dry', 'Oily', 'Combination', 'Sensitive'];
const DEFAULT_WHATSAPP_NUMBER = '+91';

/** Match API / DB values to form options (carousel labels). */
function normalizeGenderForForm(raw, optionList) {
  if (raw == null || String(raw).trim() === '') return '';
  const s = String(raw).trim();
  const base = /^male$/i.test(s) ? 'Male' : /^female$/i.test(s) ? 'Female' : s;
  if (!Array.isArray(optionList) || !optionList.length) return base;
  const match = optionList.find((o) => String(o).toLowerCase() === String(base).toLowerCase());
  return match || optionList.find((o) => String(o).toLowerCase() === s.toLowerCase()) || base;
}

function normalizeSkinTypeForForm(raw, optionList) {
  if (raw == null || String(raw).trim() === '') return null;
  const s = String(raw).trim();
  const options = Array.isArray(optionList) && optionList.length ? optionList : DEFAULT_SKIN_OPTIONS;
  const exact = options.find((o) => o === s);
  if (exact) return exact;
  const lower = s.toLowerCase();
  const ci = options.find((o) => String(o).toLowerCase() === lower);
  if (ci) return ci;
  return options.find((o) => lower.includes(String(o).toLowerCase()) || String(o).toLowerCase().includes(lower)) || null;
}

function persistSalonLastCustomer(token, payload) {
  try {
    localStorage.setItem(salonLastCustomerStorageKey(token), JSON.stringify({ ...payload, savedAt: new Date().toISOString() }));
  } catch (_) {}
}

const UserInfoPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, sessionLandingPath } = useSkinSession();
  useSkinAnalysisPlan();
  const userInfoFromState = location.state?.userInfo || null;
  const [gender, setGender] = useState(userInfoFromState?.gender || '');
  const [skinType, setSkinType] = useState(userInfoFromState?.skinType || null);
  const [age, setAge] = useState(userInfoFromState?.age ?? null);
  const [isAgeInputActive, setIsAgeInputActive] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const sessionIdRef = useRef(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const deferSession = !!location.state?.deferSession;

  const [customerMode, setCustomerMode] = useState('new');
  const [customerName, setCustomerName] = useState(userInfoFromState?.name || '');
  const [phone, setPhone] = useState(() => normalizeWhatsAppValue(userInfoFromState?.mobileNumber || userInfoFromState?.phone || '') || DEFAULT_WHATSAPP_NUMBER);
  const [phoneCountryIso2, setPhoneCountryIso2] = useState(() => getCountryIso2FromPhone(userInfoFromState?.mobileNumber || userInfoFromState?.phone || '') || DEFAULT_WHATSAPP_COUNTRY);
  const [email, setEmail] = useState(userInfoFromState?.email || '');
  const [linkedSalonRef, setLinkedSalonRef] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  /** Holds the OLD salon_reference that must be passed to the backend as previous_salon_reference
   * when creating a new session for a returning customer.
   * Set in applyLookupResult (or restore). Cleared immediately after the new session is created.
   * Kept separate from linkedSalonRef so retries don't trigger a second migration. */
  const priorRefForMigration = useRef(null);
  const [lookupError, setLookupError] = useState('');
  const [showProfileCard, setShowProfileCard] = useState(false);
  /** New customer: duplicate mobile in DB (server check). */
  const [newPhoneDuplicate, setNewPhoneDuplicate] = useState(false);
  const [newPhoneChecking, setNewPhoneChecking] = useState(false);
  const [showDuplicateMobilePopup, setShowDuplicateMobilePopup] = useState(false);
  const [sessionCreateError, setSessionCreateError] = useState('');
  const pendingLookupRef = useRef(null);

  const [questionnaire, setQuestionnaire] = useState(null);
  const [questionnaireLoading, setQuestionnaireLoading] = useState(true);
  const [questionnaireError, setQuestionnaireError] = useState(null);
  const [dynamicAnswers, setDynamicAnswers] = useState({});

  const gendersSalon = ['Male', 'Female'];

  const phoneValidByCountry = useMemo(
    () => validateWhatsAppNumberByCountry(phone, phoneCountryIso2),
    [phone, phoneCountryIso2]
  );

  useEffect(() => {
    if (customerMode !== 'new') {
      setNewPhoneChecking(false);
      return;
    }
    if (!phoneValidByCountry) {
      setNewPhoneDuplicate(false);
      setNewPhoneChecking(false);
      return;
    }
    let cancelled = false;
    setNewPhoneChecking(true);
    const t = setTimeout(() => {
      checkSalonMobileExists(phone, token)
        .then((r) => {
          if (!cancelled) setNewPhoneDuplicate(!!r.exists);
        })
        .catch(() => {
          if (!cancelled) setNewPhoneDuplicate(false);
        })
        .finally(() => {
          if (!cancelled) setNewPhoneChecking(false);
        });
    }, 450);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [phone, customerMode, phoneValidByCountry, token]);

  const applyDemographicsFromLookup = useCallback((data, qsnap) => {
    const hasQ = Array.isArray(qsnap) && qsnap.length > 0;
    const genderQ = hasQ ? qsnap.find((q) => String(q.label || '').trim().toLowerCase() === 'gender') : null;
    const skinQ = hasQ ? qsnap.find((q) => String(q.label || '').trim().toLowerCase() === 'skin type') : null;
    const ageQ = hasQ ? qsnap.find((q) => String(q.label || '').trim().toLowerCase() === 'age') : null;

    const genderOpts = Array.isArray(genderQ?.options) ? genderQ.options : null;
    const skinOpts = Array.isArray(skinQ?.options) && skinQ.options.length ? skinQ.options : DEFAULT_SKIN_OPTIONS;

    const g = normalizeGenderForForm(data.gender, genderOpts);
    const skinNorm = normalizeSkinTypeForForm(data.skin_type, skinOpts);

    if (g) setGender(g);
    if (skinNorm) setSkinType(skinNorm);
    if (data.age != null && data.age !== '') {
      const n = Number(data.age);
      if (!Number.isNaN(n)) setAge(n);
    }

    if (hasQ) {
      setDynamicAnswers((prev) => {
        const next = { ...prev };
        if (genderQ && g) next[genderQ.label || 'Gender'] = g;
        if (skinQ && skinNorm) next[skinQ.label || 'Skin Type'] = skinNorm;
        if (ageQ && data.age != null && !Number.isNaN(Number(data.age))) next[ageQ.label || 'Age'] = Number(data.age);
        return next;
      });
    }
  }, []);

  const applyLookupResult = (data) => {
    setLinkedSalonRef(data.salon_reference);
    priorRefForMigration.current = data.salon_reference;
    setCustomerName(data.name || '');
    const normalizedPhone = normalizeWhatsAppValue(data.mobile_number || '');
    setPhone(normalizedPhone || DEFAULT_WHATSAPP_NUMBER);
    setPhoneCountryIso2(getCountryIso2FromPhone(normalizedPhone || DEFAULT_WHATSAPP_NUMBER) || DEFAULT_WHATSAPP_COUNTRY);
    setEmail(data.email != null ? String(data.email) : '');

    applyDemographicsFromLookup(data, questionnaire);

    if (questionnaireLoading) {
      pendingLookupRef.current = data;
    } else {
      pendingLookupRef.current = null;
    }

    setShowProfileCard(true);
    setLookupError('');
    const ref = data.salon_reference;
    // Continue creates a new salon_reference; server migrates all rows from this lookup ref to the new id.
    sessionIdRef.current = null;
    setSessionId(null);
    try {
      localStorage.removeItem(SESSION_STORAGE_KEY);
    } catch (_) {}

    persistSalonLastCustomer(token, {
      customerMode: 'existing',
      prior_salon_reference: ref,
      salon_reference: ref,
      sessionCreated: false,
      mobile_number: data.mobile_number,
      name: data.name,
      email: data.email != null ? String(data.email) : '',
      gender: data.gender,
      skin_type: data.skin_type,
      age: data.age,
    });
  };

  useEffect(() => {
    if (questionnaireLoading || !pendingLookupRef.current) return;
    const data = pendingLookupRef.current;
    if (Array.isArray(questionnaire) && questionnaire.length > 0) {
      applyDemographicsFromLookup(data, questionnaire);
    }
    pendingLookupRef.current = null;
  }, [questionnaireLoading, questionnaire, applyDemographicsFromLookup]);

  const handleLookupExisting = async () => {
    setLookupError('');
    const p = String(phone).trim();
    if (!phoneValidByCountry) {
      setLookupError(getWhatsAppValidationMessage('selected country'));
      return;
    }
    setLookupLoading(true);
    try {
      const data = await lookupSalonCustomerByPhone(p, token);
      if (data.found) {
        applyLookupResult(data);
        return;
      }
      setLinkedSalonRef(null);
      setShowProfileCard(false);
      setLookupError('No customer found for this number.');
    } catch (e) {
      setLookupError(e.message || 'Lookup failed.');
    } finally {
      setLookupLoading(false);
    }
  };

  const handleChangeLinkedCustomer = () => {
    setShowProfileCard(false);
    setLinkedSalonRef(null);
    setSessionCreateError('');
    setCustomerName('');
    setEmail('');
    setGender('');
    setSkinType(null);
    setAge(null);
    sessionIdRef.current = null;
    setSessionId(null);
    try {
      localStorage.removeItem(SESSION_STORAGE_KEY);
    } catch (_) {}
  };

  const switchToNewCustomer = () => {
    if (customerMode === 'existing' && linkedSalonRef && showProfileCard) return;
    setCustomerMode('new');
    setLinkedSalonRef(null);
    priorRefForMigration.current = null;
    setShowProfileCard(false);
    setLookupError('');
    setNewPhoneDuplicate(false);
    setSessionCreateError('');
    sessionIdRef.current = null;
    setSessionId(null);
    try {
      localStorage.removeItem(SESSION_STORAGE_KEY);
    } catch (_) {}
  };

  const handleRegisterDifferentCustomer = () => {
    setCustomerMode('new');
    setLinkedSalonRef(null);
    priorRefForMigration.current = null;
    setShowProfileCard(false);
    setLookupError('');
    setNewPhoneDuplicate(false);
    setShowDuplicateMobilePopup(false);
    setSessionCreateError('');
    setCustomerName('');
    setEmail('');
    setPhone(DEFAULT_WHATSAPP_NUMBER);
    setPhoneCountryIso2(DEFAULT_WHATSAPP_COUNTRY);
    setGender('');
    setSkinType(null);
    setAge(null);
    setDynamicAnswers({});
    sessionIdRef.current = null;
    setSessionId(null);
    try {
      localStorage.removeItem(SESSION_STORAGE_KEY);
    } catch (_) {}
  };

  const switchToExistingCustomer = () => {
    if (customerMode === 'existing' && linkedSalonRef && showProfileCard) return;
    setCustomerMode('existing');
    setLinkedSalonRef(null);
    priorRefForMigration.current = null;
    setShowProfileCard(false);
    setCustomerName('');
    setEmail('');
    setGender('');
    setSkinType(null);
    setAge(null);
    setLookupError('');
    setNewPhoneDuplicate(false);
    setShowDuplicateMobilePopup(false);
    setSessionCreateError('');
    sessionIdRef.current = null;
    setSessionId(null);
    try {
      localStorage.removeItem(SESSION_STORAGE_KEY);
    } catch (_) {}
  };

  const hasContactQuestion = questionnaire?.some(q => String(q.type || '').toLowerCase() === 'contact');

  const contactValid = hasContactQuestion ? true : (
    customerMode === 'existing'
      ? !!linkedSalonRef &&
        phoneValidByCountry &&
        String(customerName).trim().length > 0
      : String(customerName).trim().length > 0 &&
        phoneValidByCountry &&
        !newPhoneDuplicate &&
        !newPhoneChecking
  );

  // Fetch questionnaire when token is available
  useEffect(() => {
    if (!token) {
      setQuestionnaireLoading(false);
      return;
    }
    let cancelled = false;
    setQuestionnaireLoading(true);
    setQuestionnaireError(null);
    (async () => {
      try {
        const res = await fetchWithTimeout(
          `${SESSION_APP_DATA_BASE}/${encodeURIComponent(token)}/questionnaire`,
          { timeoutMs: 20000 }
        );
        if (!res.ok) throw new Error(`Questionnaire ${res.status}`);
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        const questions = data?.questions ?? data?.data?.questions ?? (Array.isArray(data) ? data : []);
        setQuestionnaire(Array.isArray(questions) ? questions : []);
        setQuestionnaireLoading(false);
      } catch (err) {
        if (!cancelled) {
          setQuestionnaireError(err?.message || 'Failed to load questionnaire');
          setQuestionnaire(null);
          setQuestionnaireLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const lastCustomerRestoredRef = useRef(false);
  useEffect(() => {
    if (!deferSession || questionnaireLoading) return;
    if (lastCustomerRestoredRef.current) return;
    let raw;
    try {
      raw = localStorage.getItem(salonLastCustomerStorageKey(token));
    } catch (_) {
      lastCustomerRestoredRef.current = true;
      return;
    }
    if (!raw) {
      lastCustomerRestoredRef.current = true;
      return;
    }
    let saved;
    try {
      saved = JSON.parse(raw);
    } catch (_) {
      lastCustomerRestoredRef.current = true;
      return;
    }
    if (!saved || typeof saved !== 'object' || !(saved.salon_reference || saved.prior_salon_reference)) {
      lastCustomerRestoredRef.current = true;
      return;
    }
    // If the session was already created (handleContinue completed successfully), do not
    // restore into "existing customer needs Continue" mode — that would trigger another migration
    // and create a duplicate new session on the next Continue click.
    if (saved.sessionCreated === true) {
      lastCustomerRestoredRef.current = true;
      return;
    }
    // New customers also persist salon_reference; only reopen "existing" tab for a true existing flow.
    const restoreAsExistingCustomer =
      saved.customerMode === 'existing' ||
      (saved.customerMode !== 'new' && !!saved.prior_salon_reference);
    if (!restoreAsExistingCustomer) {
      lastCustomerRestoredRef.current = true;
      return;
    }
    lastCustomerRestoredRef.current = true;

    setCustomerMode('existing');
    {
      const normalized = normalizeWhatsAppValue(String(saved.mobile_number || '').trim()) || DEFAULT_WHATSAPP_NUMBER;
      setPhone(normalized);
      setPhoneCountryIso2(getCountryIso2FromPhone(normalized) || DEFAULT_WHATSAPP_COUNTRY);
    }
    setCustomerName(saved.name || '');
    setEmail(saved.email != null ? String(saved.email) : '');
    const lookupRef = String(saved.prior_salon_reference || saved.salon_reference || '').trim();
    setLinkedSalonRef(lookupRef);
    priorRefForMigration.current = lookupRef;
    setShowProfileCard(true);
    // Continue will migrate to a new session id; do not bind storage until then.
    sessionIdRef.current = null;
    setSessionId(null);
    try {
      localStorage.removeItem(SESSION_STORAGE_KEY);
    } catch (_) {}

    if (saved.gender) setGender(saved.gender);
    if (saved.skin_type) setSkinType(normalizeSkinTypeForForm(saved.skin_type, DEFAULT_SKIN_OPTIONS));
    if (saved.age != null && saved.age !== '') {
      const n = Number(saved.age);
      if (!Number.isNaN(n)) setAge(n);
    }
    if (saved.dynamicAnswers && typeof saved.dynamicAnswers === 'object') {
      setDynamicAnswers((prev) => ({ ...prev, ...saved.dynamicAnswers }));
    }
  }, [deferSession, token, questionnaireLoading]);

  // Sync dynamic answers to gender/skinType/age when questionnaire is used
  const effectiveGender = questionnaire?.length ? (dynamicAnswers['Gender'] ?? gender) : gender;
  const effectiveSkinType = questionnaire?.length ? (dynamicAnswers['Skin Type'] ?? skinType) : skinType;
  const effectiveAge = questionnaire?.length ? (dynamicAnswers['Age'] ?? age) : age;

  const getValueForQuestion = (q, idx) => {
    const label = q.label || `Question ${idx + 1}`;
    return dynamicAnswers[label] ?? (label === 'Gender' ? gender : label === 'Skin Type' ? skinType : label === 'Age' ? age : null);
  };
  const requiredQuestions = questionnaire?.length ? questionnaire.filter((q) => q.required !== false && q.key !== 'no') : [];
  const allRequiredFilled = !questionnaire?.length || requiredQuestions.every((q, idx) => {
    const v = getValueForQuestion(q, idx);
    if (String(q.type || '').toLowerCase() === 'number') return v !== null && v !== undefined && !isNaN(Number(v)) && Number(v) >= 14 && Number(v) <= 75;
    if (String(q.type || '').toLowerCase() === 'contact') {
      const contactOptions = typeof q.options === 'object' && q.options ? q.options : {};
      const nameValid = !contactOptions.Name || String(customerName).trim().length > 0;
      const phoneValid = !contactOptions.Phone || phoneValidByCountry;
      const emailValid = !contactOptions.Email || (email && email.includes('@'));
      return nameValid && phoneValid && emailValid;
    }
    return v !== null && v !== undefined && String(v).trim() !== '';
  });

  const setAnswerByLabel = (label, value) => {
    setDynamicAnswers((prev) => ({ ...prev, [label]: value }));
    if (label === 'Gender') setGender(value);
    if (label === 'Skin Type') setSkinType(value);
    if (label === 'Age') setAge(value);
  };

  // Session ID: state (from previous page) > URL (?session_id=) > localStorage. Fallback to URL keeps flow working when state is lost.
  useEffect(() => {
    if (deferSession) {
      const prev = getSessionId();
      if (prev) clearCache(prev);
      clearSession();
      sessionIdRef.current = null;
      setSessionId(null);
      return;
    }
    const fromState = location.state?.sessionId;
    const fromUrl = getSessionIdFromUrl(location);
    const fromStorage = getSessionId();
    const sessionIdFromLanding = fromState || fromUrl || fromStorage;
    if (sessionIdFromLanding) {
      sessionIdRef.current = sessionIdFromLanding;
      setSessionId(sessionIdFromLanding);
      try {
        localStorage.setItem(SESSION_STORAGE_KEY, sessionIdFromLanding);
      } catch (e) { }
      if (fromState || fromUrl) return;
    }
    const isFromLanding = !location.state?.preserveSession;
    if (isFromLanding && !fromUrl) {
      const prevSessionId = getSessionId();
      if (prevSessionId) {
        clearCache(prevSessionId);
      }
      clearSession();
    }
  }, [location, deferSession]);

  // When all fields are empty (no questions or every question has empty options), skip form and open disclaimer modal
  const skipUserInfoRef = useRef(false);
  const allFieldsEmpty = Array.isArray(questionnaire) && (
    questionnaire.length === 0 ||
    questionnaire.every((q) => !Array.isArray(q?.options) || q.options.length === 0)
  );
  useEffect(() => {
    if (deferSession) return;
    if (!token || questionnaireLoading || !allFieldsEmpty || skipUserInfoRef.current) return;
    skipUserInfoRef.current = true;
    const run = async () => {
      const API_BASE_URL = getApiBaseUrl() || '';
      let sid = sessionIdRef.current || sessionId;
      if (!sid) {
        try {
          sid = await initializeSession(API_BASE_URL, token ? { app_session_token: token } : {});
          sessionIdRef.current = sid;
          setSessionId(sid);
          if (sid) try { localStorage.setItem(SESSION_STORAGE_KEY, sid); } catch (_) {}
        } catch (_) {}
      }
      setShowInstructions(true);
    };
    run();
  }, [token, questionnaireLoading, allFieldsEmpty, sessionId, deferSession]);

  const handleContinue = async () => {
    const g = effectiveGender;
    const s = effectiveSkinType;
    const a = effectiveAge;
    const demoValid = questionnaire?.length ? allRequiredFilled : (g && s && (a != null && a >= 14 && a <= 75));
    if (!contactValid || !demoValid) return;

    setIsProcessing(true);
    setSessionCreateError('');
    try {
      const API_BASE_URL = getApiBaseUrl() || '';
      const deviceInfo = location.state?.deviceInfo || null;

      if (customerMode === 'existing' && !linkedSalonRef) {
        setSessionCreateError('Please find your profile with your mobile number before continuing.');
        setIsProcessing(false);
        return;
      }

      if (customerMode === 'new') {
        if (!phoneValidByCountry) {
          setIsProcessing(false);
          return;
        }
        const { exists } = await checkSalonMobileExists(phone, token);
        if (exists) {
          setNewPhoneDuplicate(true);
          setShowDuplicateMobilePopup(true);
          setIsProcessing(false);
          return;
        }
      }

      // Existing customer: new session id + server migrates rows from lookup ref.
      // New customer: reuse session created on landing (Start) when present; otherwise create here.
      let sid = sessionIdRef.current || sessionId;
      // Only create a new session when we have a prior migration ref (the OLD reference).
      // This prevents creating duplicate sessions if the user retries after a partial failure
      // (where the session was already created but user profile save failed).
      const migrationRef = priorRefForMigration.current;
      if (customerMode === 'existing' && migrationRef) {
        sid = await createNewSessionWithBackend(API_BASE_URL, {
          app_session_token: token || undefined,
          device_info: deviceInfo,
          previous_salon_reference: migrationRef,
        });
        // Clear immediately: if saveSalonUserProfile fails below, retry will reuse this
        // session instead of creating yet another new one.
        priorRefForMigration.current = null;
      } else if (customerMode === 'new') {
        sid = await createNewSessionWithBackend(API_BASE_URL, {
          app_session_token: token || undefined,
          device_info: deviceInfo,
        });
      }
      if (!sid) {
        setIsProcessing(false);
        return;
      }
      sessionIdRef.current = sid;
      setSessionId(sid);
      if (customerMode === 'existing') {
        setLinkedSalonRef(sid);
      }
      try {
        localStorage.setItem(SESSION_STORAGE_KEY, sid);
      } catch (_) {}

      const extra = questionnaire?.length ? dynamicAnswers : null;
      await saveSalonUserProfile(
        sid,
        {
          name: customerName,
          mobileNumber: phone,
          email,
          gender: g,
          skinType: s,
          age: a,
        },
        deviceInfo,
        token || null,
        extra,
      );
      persistSalonLastCustomer(token, {
        customerMode,
        salon_reference: sid,
        prior_salon_reference: undefined,
        sessionCreated: true,
        mobile_number: phone,
        name: customerName,
        email,
        gender: g,
        skin_type: s,
        age: a,
        dynamicAnswers: questionnaire?.length ? { ...dynamicAnswers } : undefined,
      });
      setShowInstructions(true);
    } catch (error) {
      setSessionCreateError(error?.message || 'Could not start session. Try again.');
      setIsProcessing(false);
    }
  };

  const handleInstructionsContinue = () => {
    setShowInstructions(false);
    setIsProcessing(false);
    const sidToPass = sessionIdRef.current || sessionId;
    const path = pathWithSessionId('/capture', sidToPass);
    navigate(path, {
      state: {
        gender: effectiveGender,
        skinType: effectiveSkinType,
        age: effectiveAge,
        sessionId: sidToPass,
        name: customerName,
        mobileNumber: phone,
        email: email || '',
        instagramId: '',
        ...(token && { token })
      }
    });
  };

  const handleCloseInstructions = () => {
    setShowInstructions(false);
    setIsProcessing(false);
  };

  const handleUseExistingFromDuplicatePopup = () => {
    setShowDuplicateMobilePopup(false);
    setCustomerMode('existing');
    setLinkedSalonRef(null);
    setShowProfileCard(false);
    setLookupError('');
  };

  const handleBack = () => {
    navigate(sessionLandingPath);
  };

  const handleHome = async () => {
    const prevSid = sessionIdRef.current || sessionId || getSessionId();
    if (prevSid) clearCache(prevSid);
    const API_BASE_URL = getApiBaseUrl() || '';
    await finalizeCustomerSession(API_BASE_URL, prevSid, token || null);
      priorRefForMigration.current = null;
    try {
      localStorage.removeItem(salonLastCustomerStorageKey(token));
    } catch (_) {}
    pendingLookupRef.current = null;

    setCustomerMode('new');
    setCustomerName('');
    setPhone(DEFAULT_WHATSAPP_NUMBER);
    setPhoneCountryIso2(DEFAULT_WHATSAPP_COUNTRY);
    setEmail('');
    setGender('');
    setSkinType(null);
    setAge(null);
    setDynamicAnswers({});
    setLinkedSalonRef(null);
    setShowProfileCard(false);
    setLookupError('');
    setLookupLoading(false);
    setNewPhoneDuplicate(false);
    setSessionCreateError('');
    setShowInstructions(false);
    setIsProcessing(false);
    setIsAgeInputActive(false);
    sessionIdRef.current = null;
    setSessionId(null);

    navigate(sessionLandingPath);
  };

  const lockNewCustomerTab = customerMode === 'existing' && !!linkedSalonRef && showProfileCard;

  /** Existing customer: only show gender / skin type / age after a successful Find (same timing as name/email fields). */
  const showExistingDemographics = customerMode !== 'existing' || showProfileCard;

  return (
    <div
      className="user-info-page-shell page-bg salon-user-info-page"
      id="user-info-page-shell"
    >
      {/* Navigation Buttons */}
      <div className="user-info-page-nav" id="user-info-page-nav">
        <button
          type="button"
          className="user-info-page-back-button"
          onClick={handleBack}
          aria-label="Go back"
        >
          ←
        </button>
        <div className="user-info-page-title-wrapper">
          <SalonHeaderActions homeButtonClassName="user-info-page-home-button" onHome={handleHome} />
        </div>
      </div>

      <main className="user-info-page-main" id="user-info-page-main">
        <div className="user-info-page-container" id="user-info-page-container">
          <section className="user-info-page-layout" id="user-info-page-layout">
            <p className="user-info-page-subtitle" id="user-info-page-subtitle">Enter customer details for this session</p>
            {sessionCreateError ? (
              <p className="user-info-lookup-error" role="alert" id="user-info-session-error">
                {sessionCreateError}
              </p>
            ) : null}

            <div className="user-info-form" id="user-info-form">
              {questionnaireLoading && (
                <div className="user-info-loading-overlay" id="user-info-loading-overlay" aria-busy="true" aria-live="polite">
                  <div className="user-info-loading-content">
                    <div className="loading-spinner" aria-hidden="true" />
                    <p className="loading-text">Loading options…</p>
                  </div>
                </div>
              )}
              {!questionnaireLoading && questionnaireError && !questionnaire?.length && (
                <p className="user-info-page-subtitle" style={{ color: 'var(--error, #b91c1c)' }}>{questionnaireError}</p>
              )}

              <div className={`user-info-form-body ${isProcessing ? 'user-info-form-blocked' : ''}`}>
                {!questionnaireLoading && (
                  <div className="user-info-customer-block" id="user-info-customer-block">
                    <div className="user-info-segmented" role="tablist" aria-label="Customer type">
                      <button
                        type="button"
                        role="tab"
                        aria-selected={customerMode === 'new'}
                        disabled={lockNewCustomerTab}
                        className={`user-info-segment ${customerMode === 'new' ? 'user-info-segment-active' : ''}${lockNewCustomerTab ? ' user-info-segment-locked' : ''}`}
                        onClick={switchToNewCustomer}
                      >
                        New Customer
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={customerMode === 'existing'}
                        className={`user-info-segment ${customerMode === 'existing' ? 'user-info-segment-active' : ''}`}
                        onClick={switchToExistingCustomer}
                      >
                        Existing Customer
                      </button>
                    </div>
                    {lockNewCustomerTab ? (
                      <p className="user-info-different-customer-hint">
                        <button
                          type="button"
                          className="user-info-text-button"
                          onClick={handleRegisterDifferentCustomer}
                          title="Creates a new customer session in the database"
                        >
                          Register a different customer
                        </button>
                      </p>
                    ) : null}

                    {customerMode === 'new' && !hasContactQuestion && (
                      <>
                        <div className="user-info-section user-info-section-contact">
                          <label className="user-info-label" htmlFor="user-info-name">Name <span className="required">*</span></label>
                          <input
                            id="user-info-name"
                            type="text"
                            className="user-info-glass-input"
                            value={customerName}
                            onChange={(e) => setCustomerName(e.target.value)}
                            placeholder="Enter customer name"
                            autoComplete="name"
                          />
                        </div>
                        <div className="user-info-section user-info-section-contact">
                          <label className="user-info-label" htmlFor="user-info-phone-new">WhatsApp Number <span className="required">*</span></label>
                          <CountryWhatsAppInput
                            id="user-info-phone-new"
                            name="user-info-phone-new"
                            value={phone}
                            defaultCountry={phoneCountryIso2}
                            placeholder="Enter WhatsApp number"
                            inputClassName="user-info-glass-input"
                            onChange={(nextPhone, meta) => {
                              setPhone(nextPhone || DEFAULT_WHATSAPP_NUMBER);
                              setPhoneCountryIso2(meta?.countryIso2 || DEFAULT_WHATSAPP_COUNTRY);
                              setNewPhoneDuplicate(false);
                              setShowDuplicateMobilePopup(false);
                            }}
                            onBlur={(nextPhone, meta) => {
                              setPhone(nextPhone || DEFAULT_WHATSAPP_NUMBER);
                              setPhoneCountryIso2(meta?.countryIso2 || DEFAULT_WHATSAPP_COUNTRY);
                            }}
                          />
                          {!!phone && !phoneValidByCountry ? (
                            <p className="user-info-lookup-error" role="alert">
                              {getWhatsAppValidationMessage('selected country')}
                            </p>
                          ) : null}
                          {newPhoneChecking && phoneValidByCountry ? (
                            <p className="user-info-page-subtitle" style={{ marginTop: '0.35rem', fontSize: '0.85rem' }}>
                              Checking number…
                            </p>
                          ) : null}
                          {newPhoneDuplicate ? (
                            <p className="user-info-lookup-error" role="alert">
                              This WhatsApp number is already registered. Enter a different number or use &quot;Existing customer&quot; to look up their profile.
                            </p>
                          ) : null}
                        </div>
                        <div className="user-info-section user-info-section-contact">
                          <label className="user-info-label" htmlFor="user-info-email-new">Email</label>
                          <input
                            id="user-info-email-new"
                            type="email"
                            className="user-info-glass-input"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Enter email"
                            autoComplete="email"
                          />
                        </div>
                      </>
                    )}

                    {customerMode === 'existing' && (
                      <>
                        {!showProfileCard ? (
                          <div className="user-info-section user-info-section-contact">
                            <label className="user-info-label" htmlFor="user-info-phone-existing">WhatsApp Number <span className="required">*</span></label>
                            <div className="user-info-phone-lookup-row">
                              <div className="user-info-phone-lookup-input-wrap">
                                <CountryWhatsAppInput
                                  id="user-info-phone-existing"
                                  name="user-info-phone-existing"
                                  value={phone}
                                  defaultCountry={phoneCountryIso2}
                                  placeholder="Enter registered WhatsApp number"
                                  inputClassName="user-info-glass-input"
                                  onChange={(nextPhone, meta) => {
                                    setPhone(nextPhone || DEFAULT_WHATSAPP_NUMBER);
                                    setPhoneCountryIso2(meta?.countryIso2 || DEFAULT_WHATSAPP_COUNTRY);
                                    setLookupError('');
                                  }}
                                  onBlur={(nextPhone, meta) => {
                                    setPhone(nextPhone || DEFAULT_WHATSAPP_NUMBER);
                                    setPhoneCountryIso2(meta?.countryIso2 || DEFAULT_WHATSAPP_COUNTRY);
                                  }}
                                />
                              </div>
                              <button
                                type="button"
                                className="user-info-lookup-button"
                                onClick={handleLookupExisting}
                                disabled={lookupLoading}
                              >
                                {lookupLoading ? '…' : 'Find'}
                              </button>
                            </div>
                            {lookupError ? <p className="user-info-lookup-error" role="alert">{lookupError}</p> : null}
                          </div>
                        ) : (
                          <>
                            <div className="user-info-profile-card">
                              <div className="user-info-profile-text">
                                <span className="user-info-profile-name">{customerName || '—'}</span>
                                <span className="user-info-profile-phone">{phone || '—'}</span>
                              </div>
                              <button type="button" className="user-info-profile-change" onClick={handleChangeLinkedCustomer}>
                                Change
                              </button>
                            </div>
                            {!hasContactQuestion && (
                              <>
                                <div className="user-info-section user-info-section-contact">
                                  <label className="user-info-label" htmlFor="user-info-name-existing">Name <span className="required">*</span></label>
                                  <input
                                    id="user-info-name-existing"
                                    type="text"
                                    className="user-info-glass-input"
                                    value={customerName}
                                    onChange={(e) => setCustomerName(e.target.value)}
                                    placeholder="Enter customer name"
                                    autoComplete="name"
                                  />
                                </div>
                                <div className="user-info-section user-info-section-contact">
                                  <label className="user-info-label" htmlFor="user-info-phone-edit">WhatsApp Number <span className="required">*</span></label>
                                  <CountryWhatsAppInput
                                    id="user-info-phone-edit"
                                    name="user-info-phone-edit"
                                    value={phone}
                                    defaultCountry={phoneCountryIso2}
                                    placeholder="Enter WhatsApp number"
                                    inputClassName="user-info-glass-input"
                                    onChange={(nextPhone, meta) => {
                                      setPhone(nextPhone || DEFAULT_WHATSAPP_NUMBER);
                                      setPhoneCountryIso2(meta?.countryIso2 || DEFAULT_WHATSAPP_COUNTRY);
                                    }}
                                    onBlur={(nextPhone, meta) => {
                                      setPhone(nextPhone || DEFAULT_WHATSAPP_NUMBER);
                                      setPhoneCountryIso2(meta?.countryIso2 || DEFAULT_WHATSAPP_COUNTRY);
                                    }}
                                  />
                                </div>
                                <div className="user-info-section user-info-section-contact">
                                  <label className="user-info-label" htmlFor="user-info-email-existing">Email</label>
                                  <input
                                    id="user-info-email-existing"
                                    type="email"
                                    className="user-info-glass-input"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="Enter email"
                                    autoComplete="email"
                                  />
                                </div>
                              </>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </div>
                )}

                {token && allFieldsEmpty && !deferSession ? (
                  <div className="user-info-loading-overlay" id="user-info-skip-overlay" aria-busy="true" aria-live="polite">
                    <div className="user-info-loading-content">
                      <div className="loading-spinner" aria-hidden="true" />
                      <p className="loading-text">Loading…</p>
                    </div>
                  </div>
                ) : questionnaire?.length > 0 ? (
                  <>
                    {[...questionnaire]
                      .sort((a, b) => {
                        const getOrder = (q) => {
                          const type = String(q.type || '').toLowerCase();
                          const label = String(q.label || '').toLowerCase();
                          if (type === 'contact') return 1;
                          if (label === 'gender') return 2;
                          if (label === 'age') return 3;
                          if (label === 'skin type') return 4;
                          return (q.order ?? 0) + 10;
                        };
                        return getOrder(a) - getOrder(b);
                      })
                      .map((q, idx) => {
                        if (q.key === 'no') return null;

                        const label = q.label || `Question ${idx + 1}`;
                        if (!showExistingDemographics) {
                          const dem = String(label).trim().toLowerCase();
                          if (dem === 'gender' || dem === 'skin type' || dem === 'age' || dem === 'contact details') return null;
                        }
                        const options = Array.isArray(q.options) ? q.options : [];
                        const normalizedType = String(q.type || '').toLowerCase();
                        const isSelectLike = normalizedType === 'multi-select' || normalizedType === 'yes_no' || normalizedType === 'single-select' || normalizedType === 'select';
                        const value = dynamicAnswers[label] ?? (label === 'Gender' ? gender : label === 'Skin Type' ? skinType : label === 'Age' ? age : null);
                        const required = q.required !== false;

                        if (normalizedType === 'number') {
                          const defaultNum = typeof q.options === 'number' ? q.options : 18;
                          const numValue = value !== null && value !== undefined && value !== '' ? Number(value) : null;
                          return (
                            <div key={`${label}-${idx}`} className="user-info-section" id={`user-info-section-${label.replace(/\s+/g, '-')}`}>
                              <label className="user-info-label">
                                {label} {required && <span className="required">*</span>}
                              </label>
                              <div className="user-info-age-selector-container">
                                <div className="user-info-age-selector">
                                  <button
                                    type="button"
                                    className="user-info-age-selector-button user-info-age-decrement"
                                    onClick={() => {
                                      const current = numValue ?? defaultNum;
                                      if (current > 14) setAnswerByLabel(label, current - 1);
                                    }}
                                    aria-label="Decrease"
                                  >−</button>
                                  <div className="user-info-age-display">
                                    {isAgeInputActive && label === 'Age' ? (
                                      <input
                                        type="number"
                                        className="user-info-age-input"
                                        min="14"
                                        max="75"
                                        value={numValue ?? ''}
                                        placeholder="Enter age (14-75)"
                                        autoFocus
                                        onChange={(e) => {
                                          const v = e.target.value;
                                          if (v === '') { setAnswerByLabel(label, null); return; }
                                          const n = parseInt(v, 10);
                                          if (!isNaN(n)) setAnswerByLabel(label, n);
                                        }}
                                        onBlur={() => {
                                          const n = numValue !== null && numValue !== undefined ? Number(numValue) : null;
                                          if (n !== null && (n < 14 || n > 75)) setAnswerByLabel(label, Math.max(14, Math.min(75, n)));
                                          setIsAgeInputActive(false);
                                        }}
                                      />
                                    ) : (
                                      <span
                                        className={`user-info-age-value ${(numValue === null || numValue === undefined) ? 'user-info-age-value-placeholder' : ''}`}
                                        onClick={() => label === 'Age' && setIsAgeInputActive(true)}
                                      >
                                        {numValue !== null && numValue !== undefined ? numValue : 'Select'}
                                      </span>
                                    )}
                                  </div>
                                  <button
                                    type="button"
                                    className="user-info-age-selector-button user-info-age-increment"
                                    onClick={() => {
                                      const current = numValue ?? defaultNum;
                                      if (current < 75) setAnswerByLabel(label, current + 1);
                                    }}
                                    aria-label="Increase"
                                  >+</button>
                                </div>
                              </div>
                            </div>
                          );
                        }

                        if (isSelectLike) {
                          if (options.length === 0) {
                            return (
                              <div key={`${label}-${idx}`} className="user-info-section" id={`user-info-section-${label.replace(/\s+/g, '-')}`}>
                                <label className="user-info-label" id={`user-info-label-${label.replace(/\s+/g, '-')}`}>
                                  {label} {required && <span className="required">*</span>}
                                </label>
                                <input
                                  type="text"
                                  className="user-info-text-input"
                                  value={value ?? ''}
                                  onChange={(e) => setAnswerByLabel(label, e.target.value)}
                                  placeholder={`Enter ${label}`}
                                />
                              </div>
                            );
                          }
                          if (label === 'Skin Type' && options.length > 0) {
                            return (
                              <div key={`${label}-${idx}`} className="user-info-section" id="user-info-section-skin-type">
                                <div className="user-info-row-header">
                                  <label className="user-info-label" id="user-info-label-skin-type">
                                    {label} {required && <span className="required">*</span>}
                                  </label>
                                  <span className="user-info-help-text">Swipe or use arrows to select</span>
                                </div>
                                <div className="user-info-carousel-wrapper">
                                  <SkinTypeCarousel
                                    options={options}
                                    selectedType={value ?? null}
                                    onSelect={(v) => setAnswerByLabel(label, v)}
                                  />
                                </div>
                              </div>
                            );
                          }
                          const isGender = label === 'Gender';
                          return (
                            <div key={`${label}-${idx}`} className="user-info-section" id={`user-info-section-${label.replace(/\s+/g, '-')}`}>
                              <label className="user-info-label" id={`user-info-label-${label.replace(/\s+/g, '-')}`}>
                                {label} {required && <span className="required">*</span>}
                              </label>
                              <div className={`user-info-options ${isGender ? 'user-info-options-gender' : ''}`} id={isGender ? 'user-info-options-gender' : undefined}>
                                {options.map((opt) => (
                                  <button
                                    key={opt}
                                    type="button"
                                    className={`user-info-option ${isGender ? 'user-info-option-gender' : ''} ${value === opt ? 'user-info-option-selected' : ''}`}
                                    id={isGender ? `user-info-option-gender-${String(opt).toLowerCase().replace(/\s+/g, '-')}` : undefined}
                                    onClick={() => setAnswerByLabel(label, opt)}
                                  >
                                    {isGender && (
                                      <img
                                        src={`${process.env.PUBLIC_URL}/gender/${genderImageFile(opt)}`}
                                        alt={opt}
                                        className="user-info-gender-image"
                                      />
                                    )}
                                    <span className="user-info-gender-label">{opt}</span>
                                  </button>
                                ))}
                              </div>
                            </div>
                          );
                        }

                        if (normalizedType === 'contact') {
                          const contactOptions = typeof q.options === 'object' && q.options ? q.options : {};
                          return (
                            <div key={`${label}-${idx}`} className="user-info-section user-info-section-contact">
                              {contactOptions.Name && (
                                <>
                                  <label className="user-info-label" htmlFor={`user-info-name-${idx}`}>Name {required && <span className="required">*</span>}</label>
                                  <input
                                    id={`user-info-name-${idx}`}
                                    type="text"
                                    className="user-info-glass-input"
                                    value={customerName}
                                    onChange={(e) => setCustomerName(e.target.value)}
                                    placeholder="Enter customer name"
                                    autoComplete="name"
                                  />
                                </>
                              )}
                              {contactOptions.Phone && (
                                <>
                                  <label className="user-info-label" htmlFor={`user-info-phone-${idx}`}>WhatsApp Number {required && <span className="required">*</span>}</label>
                                  <CountryWhatsAppInput
                                    id={`user-info-phone-${idx}`}
                                    name={`user-info-phone-${idx}`}
                                    value={phone}
                                    defaultCountry={phoneCountryIso2}
                                    placeholder="Enter WhatsApp number"
                                    inputClassName="user-info-glass-input"
                                    onChange={(nextPhone, meta) => {
                                      setPhone(nextPhone || DEFAULT_WHATSAPP_NUMBER);
                                      setPhoneCountryIso2(meta?.countryIso2 || DEFAULT_WHATSAPP_COUNTRY);
                                    }}
                                    onBlur={(nextPhone, meta) => {
                                      setPhone(nextPhone || DEFAULT_WHATSAPP_NUMBER);
                                      setPhoneCountryIso2(meta?.countryIso2 || DEFAULT_WHATSAPP_COUNTRY);
                                    }}
                                  />
                                </>
                              )}
                              {contactOptions.Email && (
                                <>
                                  <label className="user-info-label" htmlFor={`user-info-email-${idx}`}>Email {contactOptions.Email && <span className="required">*</span>}</label>
                                  <input
                                    id={`user-info-email-${idx}`}
                                    type="email"
                                    className="user-info-glass-input"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="Enter email"
                                    autoComplete="email"
                                  />
                                </>
                              )}
                            </div>
                          );
                        }

                        return null;
                      })}
                    <div className="skin-page-actions" id="user-info-actions">
                      <button
                        type="button"
                        className={`skin-action-button skin-action-button-large skin-action-button-rounded ${!allRequiredFilled || !contactValid || isProcessing ? 'disabled' : ''} ${isProcessing ? 'user-info-continue-button-processing' : ''}`}
                        id="user-info-continue-button"
                        onClick={handleContinue}
                        disabled={!allRequiredFilled || !contactValid || isProcessing}
                      >
                        {isProcessing ? 'Processing...' : 'Continue'}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    {showExistingDemographics ? (
                      <>
                        <div className="user-info-section" id="user-info-section-gender">
                          <label className="user-info-label" id="user-info-label-gender">Gender <span className="required">*</span></label>
                          <div className="user-info-options user-info-options-gender" id="user-info-options-gender">
                            {gendersSalon.map((g) => (
                              <button
                                key={g}
                                type="button"
                                className={`user-info-option user-info-option-gender ${gender === g ? 'user-info-option-selected' : ''}`}
                                id={`user-info-option-gender-${g.toLowerCase()}`}
                                onClick={() => setGender(g)}
                              >
                                <img src={`${process.env.PUBLIC_URL}/gender/${genderImageFile(g)}`} alt={g} className="user-info-gender-image" />
                                <span className="user-info-gender-label">{g}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                        <div className="user-info-section" id="user-info-section-age">
                          <label className="user-info-label" id="user-info-label-age">Age <span className="required">*</span></label>
                          <div className="user-info-age-selector-container">
                            <div className="user-info-age-selector">
                              <button type="button" className="user-info-age-selector-button user-info-age-decrement" onClick={() => { const c = age ?? 22; if (c > 14) setAge(c - 1); }} aria-label="Decrease age">−</button>
                              <div className="user-info-age-display">
                                {isAgeInputActive ? (
                                  <input type="number" className="user-info-age-input" min="14" max="75" value={age ?? ''} placeholder="Enter age (14-75)" autoFocus onChange={(e) => { const v = e.target.value; if (v === '') { setAge(null); return; } const n = parseInt(v, 10); if (!isNaN(n)) setAge(n); }} onBlur={() => { const a = age === '' || age === null || age === undefined ? null : Number(age); setAge(a === null || a < 14 || a > 75 ? null : Math.max(14, Math.min(75, a))); setIsAgeInputActive(false); }} onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { setIsAgeInputActive(false); setAge(null); } }} />
                                ) : (
                                  <span className={`user-info-age-value ${(age === null || age === undefined) ? 'user-info-age-value-placeholder' : ''}`} onClick={() => setIsAgeInputActive(true)} title="Click to enter age manually">{age !== null && age !== undefined ? age : 'Select age'}</span>
                                )}
                              </div>
                              <button type="button" className="user-info-age-selector-button user-info-age-increment" onClick={() => { const c = age ?? 22; if (c < 75) setAge(c + 1); }} aria-label="Increase age">+</button>
                            </div>
                          </div>
                        </div>
                        <div className="user-info-section" id="user-info-section-skin-type">
                          <div className="user-info-row-header">
                            <label className="user-info-label" id="user-info-label-skin-type">Skin Type <span className="required">*</span></label>
                            <span className="user-info-help-text">Swipe or use arrows to select</span>
                          </div>
                          <div className="user-info-carousel-wrapper">
                            <SkinTypeCarousel selectedType={skinType} onSelect={setSkinType} />
                          </div>
                          {skinType && (
                            <p className="user-info-skin-description">
                              {{ 'Normal': 'Balanced, clear, not sensitive', 'Dry': 'Rough, flaky, feels tight', 'Oily': 'Shiny, enlarged pores, prone to acne', 'Combination': 'Oily T-zone (forehead, nose), dry cheeks', 'Sensitive': 'Redness, itching, burning, reactive' }[skinType]}
                            </p>
                          )}
                        </div>
                      </>
                    ) : null}
                    <div className="skin-page-actions" id="user-info-actions">
                      <button type="button" className={`skin-action-button skin-action-button-large skin-action-button-rounded ${!gender || !skinType || !age || age < 14 || age > 75 || !contactValid || isProcessing ? 'disabled' : ''} ${isProcessing ? 'user-info-continue-button-processing' : ''}`} id="user-info-continue-button" onClick={handleContinue} disabled={!gender || !skinType || !age || age < 14 || age > 75 || !contactValid || isProcessing}>
                        {isProcessing ? 'Processing...' : 'Continue'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </section>
        </div>
      </main>

      <PageFooter />

      <InstructionsModal
        isOpen={showInstructions}
        onClose={handleCloseInstructions}
        onContinue={handleInstructionsContinue}
        token={token}
      />

      {showDuplicateMobilePopup ? (
        <div className="user-info-duplicate-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="user-info-duplicate-modal-title">
          <div className="user-info-duplicate-modal-card">
            <div className="user-info-duplicate-modal-badge" aria-hidden="true">!</div>
            <h3 id="user-info-duplicate-modal-title" className="user-info-duplicate-modal-title">
              WhatsApp Number Already Exists
            </h3>
            <p className="user-info-duplicate-modal-text">
              The WhatsApp number {phone ? <strong>{phone}</strong> : 'you entered'} is already registered.
              Use Existing Customer to continue with that profile, or enter a different number.
            </p>
            <div className="user-info-duplicate-modal-actions">
              <button
                type="button"
                className="user-info-duplicate-modal-btn user-info-duplicate-modal-btn-secondary"
                onClick={() => setShowDuplicateMobilePopup(false)}
              >
                Enter Different Number
              </button>
              <button
                type="button"
                className="user-info-duplicate-modal-btn user-info-duplicate-modal-btn-primary"
                onClick={handleUseExistingFromDuplicatePopup}
              >
                Use Existing Customer
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default UserInfoPage;

